import {MenuItem} from "@mui/material";
import {BaseSelect} from "components/BaseSelect";
import {useMessages} from "i18n";
import {FEEDBACK_TOPICS, type FeedbackTopic, isFeedbackTopic} from "core/support/feedbackTopic";

/**
 * The "what is this about?" control (ui#1085) — ONE component rendered by BOTH the public contact
 * form (`feedback/Feedback.tsx`) and the authenticated request flow (`support/SupportRequestDialog.tsx`).
 *
 * Shared rather than duplicated because every part of it is the same on both sides of the sign-in
 * line: the option list and its order come from `core/support/feedbackTopic.ts`, the labels from the
 * single `feedbackTopics` catalog namespace, and the house field surface from {@link BaseSelect}. The
 * two dialogs keep their separate api contracts and their separate copy; only this question is shared,
 * which is exactly what stops the two forms from drifting into two different topic lists.
 *
 * Dumb by construction (epic #26): value in, choice out, no rules and no api.
 */
interface FeedbackTopicSelectProps {
	/**
	 * A stable, locale-independent id for the label association. Supplied by the caller rather than
	 * generated because `BaseSelect` would otherwise derive one per render tree, and the two dialogs
	 * want ids that read as theirs (`feedback-topic` / `support-topic`) in a DOM dump.
	 */
	id: string;
	/** The chosen topic, or `null` for the "not stated" row. */
	value: FeedbackTopic | null;
	onChange: (value: FeedbackTopic | null) => void;
	disabled?: boolean;
}

/**
 * What "no topic" is on the wire of a `<select>`. MUI treats `null`/`undefined` as UNCONTROLLED and
 * warns, so the absent state needs a real option value; the empty string is the one value no topic
 * can collide with (`isFeedbackTopic("")` is false).
 */
const NO_TOPIC_VALUE = "";

export function FeedbackTopicSelect({id, value, onChange, disabled}: FeedbackTopicSelectProps) {
	const m = useMessages();
	return (
		<BaseSelect
			id={id}
			label={m.feedbackTopics.label}
			value={value ?? NO_TOPIC_VALUE}
			// Both halves of MUI's placeholder pairing, and neither works alone. `displayEmpty` renders
			// the empty row's text instead of collapsing to a zero-width space; `shrink` lifts the label
			// off it, because MUI derives the floating state from the VALUE (`""` reads as not filled) and
			// would otherwise draw "What is this about?" straight over "Choose a topic (optional)" in the
			// state every open of both dialogs starts in.
			displayEmpty
			shrink
			disabled={disabled}
			onChange={(event: {target: {value: unknown}}) => {
				const chosen = String(event.target.value);
				// Narrowed through the domain guard rather than cast: a stale option value then falls back
				// to "not stated" instead of reaching the api as a tag nobody triages on.
				onChange(isFeedbackTopic(chosen) ? chosen : null);
			}}
		>
			<MenuItem value={NO_TOPIC_VALUE}>{m.feedbackTopics.none}</MenuItem>
			{FEEDBACK_TOPICS.map(topic => (
				<MenuItem key={topic} value={topic}>
					{m.feedbackTopics.options[topic]}
				</MenuItem>
			))}
		</BaseSelect>
	);
}
